/**
 * ========================================
 * TRIAL BANNER
 * ========================================
 * Persistent top-bar strip showing free-trial/grace-period status, rendered
 * above the app Header in MainLayout. Sourced from the user's real
 * subscription (AuthContext -> subscriptionService.getCurrent(), see
 * TrialService.getTrialStatus) -- not a mock/local calculation.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { useTheme } from '@context/ThemeContext';

export const TrialBanner: React.FC = () => {
  const { user, trialStatus } = useAuth();
  const { themeConfig: tc } = useTheme();
  const navigate = useNavigate();

  if (!user || !trialStatus) return null;
  if (!trialStatus.isInTrial && !trialStatus.isInGracePeriod) return null;

  // "After one week, ask you to upgrade" -- days 1-6 read as a calm nudge,
  // day 7+ (daysRemaining <= 7 of a 14-day trial) switches to a stronger tone.
  const isUrgent = trialStatus.isInGracePeriod || trialStatus.daysRemaining <= 7;

  return (
    <div
      className={`w-full px-4 py-2 flex items-center justify-center gap-3 text-sm flex-wrap ${
        isUrgent ? 'bg-amber-500 text-black' : `${tc.accent.primary} text-white`
      }`}
    >
      <span className="text-base leading-none">{trialStatus.isInGracePeriod ? '⏰' : '⭐'}</span>
      <span className="font-medium text-center">
        {trialStatus.isInGracePeriod
          ? `Grace period active — ${trialStatus.graceDaysRemaining} day${trialStatus.graceDaysRemaining === 1 ? '' : 's'} left. Upgrade to keep access.`
          : `Free trial — ${trialStatus.daysRemaining} day${trialStatus.daysRemaining === 1 ? '' : 's'} remaining.`}
      </span>
      <button
        onClick={() => navigate('/pricing')}
        className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${
          isUrgent ? 'bg-black/80 text-white hover:bg-black' : 'bg-white/20 hover:bg-white/30'
        }`}
      >
        Upgrade Now
      </button>
    </div>
  );
};

export default TrialBanner;
